/**
 * Modelo de Dominio: Persona
 * 
 * Esta es la entidad de dominio del frontend Angular.
 * Corresponde a PersonaResponse del backend (Spring Boot).
 * 
 * Arquitectura DDD Frontend:
 * - domain/model/Persona → Entidad de dominio (esta clase)
 * - domain/service/PersonaRepository → Puerto abstracto
 * - infrastructure/adapters/HttpPersonaRepository → Adaptador HTTP
 * 
 * Separación del Backend:
 * - PersonaEntity (R2DBC) → Mapeo a BD
 * - Persona (Domain) → Lógica de negocio
 * - PersonaResponse (Web) → DTO para API REST ← Lo que recibe este frontend
 * 
 * @author Emerson Rodriguez - Ingeneo
 */
export interface Persona {
  // Primary key
  cdpersona?: number;
  nmconsecutivo?: number;
  
  // Identificación
  cdtipo_identificacion?: string;
  nmidentificacion?: string;
  cddigito_verificacion?: string;
  
  // Nombres persona natural
  dsnombre1?: string;
  dsnombre2?: string;
  dsapellido1?: string;
  dsapellido2?: string;
  
  // Datos personales
  cdsexo?: string;
  fenacimiento?: Date | string;
  dsemail?: string;
  
  // Datos persona jurídica
  dsnombre_comercial?: string;
  dsnombre_legal?: string;
  nmtrabajadores?: number;
  cdtipo_empresa?: string;
  
  // Ubicación y empresa
  cdmunicipio_nacim?: string;
  cdtamanio_empresa?: string;
  cdactividad_economica?: string;
  dni?: string;
  
  // Estado
  snactivo?: string;
  
  // Auditoría
  feingresa?: Date | string;
  femodifica?: Date | string;
  febaja?: Date | string;
  dni_ingresa?: string;
  dni_modifica?: string;
  
  // ========== Campos calculados por el backend ==========
  
  /** Nombre completo concatenado (calculado en backend) */
  nombreCompleto?: string;

  /** Indica si está activo como boolean (calculado en backend) */
  activo?: boolean;

  /**
   * Descripción del tipo de identificación (JOIN TPSC_TIPOS_IDENTIFICACION,
   * calculada en el backend — ver `Persona.dstipoIdentificacion` en el
   * dominio backend). Usar en vez de `cdtipo_identificacion` para mostrar
   * al usuario (ej. columna "Tipo Identificación" del buscador de persona).
   */
  dstipoIdentificacion?: string;
}

/**
 * Filtros de búsqueda para personas
 * Corresponde a los parámetros del endpoint GET /api/personas
 * Los campos cambian según el tipo de identificación:
 * - Persona Natural (CC, CE, etc.): nombre1, nombre2, apellido1, apellido2
 * - Persona Jurídica (NIT): nombreComercial
 */
export interface PersonaFiltro {
  tipoId?: string;          // Tipo de identificación (C, E, N, etc.)
  numId?: string;           // Número de identificación
  nombre1?: string;         // Primer nombre (persona natural)
  nombre2?: string;         // Segundo nombre (persona natural)
  apellido1?: string;       // Primer apellido (persona natural)
  apellido2?: string;       // Segundo apellido (persona natural)
  nombreComercial?: string; // Nombre comercial (persona jurídica - NIT)
  activo?: string;          // Estado: 'S' = Activo, 'N' = Inactivo, '' = Todos
}

/**
 * Crea un filtro vacío con valores por defecto
 */
export function createEmptyFiltro(): PersonaFiltro {
  return {
    tipoId: '',
    numId: '',
    nombre1: '',
    nombre2: '',
    apellido1: '',
    apellido2: '',
    nombreComercial: '',
    activo: ''  // Todos por defecto
  };
}

/**
 * DTO para crear/actualizar persona (sin campos de auditoría ni ID)
 * Corresponde a PersonaRequest del backend
 */
export interface PersonaRequest {
  cdtipo_identificacion?: string;
  nmidentificacion?: string;
  cddigito_verificacion?: string;
  dsnombre1?: string;
  dsnombre2?: string;
  dsapellido1?: string;
  dsapellido2?: string;
  cdsexo?: string;
  fenacimiento?: Date | string;
  dsemail?: string;
  dsnombre_comercial?: string;
  dsnombre_legal?: string;
  nmtrabajadores?: number;
  cdtipo_empresa?: string;
  cdmunicipio_nacim?: string;
  cdtamanio_empresa?: string;
  cdactividad_economica?: string;
  snactivo?: string;
}

/**
 * Crea una persona vacía con valores por defecto
 */
export function createEmptyPersona(): Persona {
  return {
    cdtipo_identificacion: 'C',  // C = Cédula (código de BD)
    nmidentificacion: '',
    dsnombre1: '',
    dsnombre2: '',
    dsapellido1: '',
    dsapellido2: '',
    cdsexo: '',
    snactivo: 'S'
  };
}

/**
 * Helper para obtener nombre completo
 * NOTA: Si el backend ya devuelve nombreCompleto, usar ese campo directamente
 */
export function getNombreCompleto(persona: Persona): string {
  // Si viene del backend, usar el campo calculado
  if (persona.nombreCompleto) {
    return persona.nombreCompleto;
  }
  // Fallback: calcular en frontend
  const parts = [
    persona.dsnombre1,
    persona.dsnombre2,
    persona.dsapellido1,
    persona.dsapellido2
  ].filter(Boolean);
  return parts.join(' ').trim();
}

/**
 * Helper para verificar si está activo
 * NOTA: Si el backend ya devuelve activo como boolean, usar ese campo
 */
export function isActivo(persona: Persona): boolean {
  // Si viene del backend como boolean, usarlo
  if (typeof persona.activo === 'boolean') {
    return persona.activo;
  }
  // Fallback: calcular desde snactivo
  return persona.snactivo?.toUpperCase() === 'S';
}

/**
 * Código de identificación NIT (persona jurídica)
 * Usado para lógica condicional de campos en formularios
 */
export const TIPO_NIT = 'N';

/**
 * Código de identificación Cédula de Ciudadanía (persona natural).
 * Legacy: `Constants.LIST_VALUE_TYPE_ID_CC = "C"`, usado como valor por
 * defecto del combo Tipo Identificación en `reset()` de varias pantallas
 * (ej. `ConsultarPersonasViewImpl.java:422`,
 * `Utilidades.getDefaultValueIndex(lbTipoIdPersona, Constants.LIST_VALUE_TYPE_ID_CC)`).
 */
export const TIPO_CEDULA = 'C';

/**
 * NOTA: Constantes centralizadas en src/app/common/constants-ejb.ts
 * - TIPOS_SEXO: Tipos de sexo (M/F) - hardcodeado como en legacy
 * - TIPO_IDENTIFICACION: Tipos de identificación
 * - Tipos de empresa y tamaños: Se cargan de BD via CatalogoRepository
 */

// ============================================================
// ALIAS DEPRECATED - Para compatibilidad durante migración
// TODO: Eliminar después de migrar todos los componentes
// ============================================================

/**
 * @deprecated Usar Persona en su lugar
 */
export type Tpsc_personasdto = Persona;
